/**
 * A hand-drawn 16px icon set.
 *
 * Bundling an icon library for a set this size would ship a few hundred KB to
 * save an hour. These are all on the same 16-unit grid with the same 1.5
 * stroke, which is what actually makes an icon set feel like one. (The count
 * used to be written out here as "eighteen", and was wrong for most of this
 * file's life — a number nobody updates is worse than no number.)
 *
 * FOUR SIZES, and only four: 9 (the micro carets riding chip labels), 13 (the
 * small-control tier, optically tuned against 12.5px text), 15 (the dock and
 * other primary controls), 18 (headline marks). The stroke scales with the
 * box, so every ad-hoc size is a different stroke weight — the set once
 * rendered at ten sizes between 8 and 20 and the "same 1.5 stroke" premise
 * above was quietly false at most call sites. A new call site picks the
 * nearest of the four; a fifth size is a decision about the whole set, made
 * here, not at the call.
 */

import type { ReactElement, SVGProps } from 'react'

export type IconName =
  | 'pathways'
  | 'history'
  | 'arrowUp'
  | 'file'
  | 'plus'
  | 'minus'
  | 'search'
  | 'sun'
  | 'moon'
  | 'key'
  | 'lock'
  | 'settings'
  | 'panelLeft'
  | 'panelRight'
  | 'close'
  | 'check'
  | 'alert'
  | 'info'
  | 'deepen'
  | 'challenge'
  | 'diverge'
  | 'experiment'
  | 'invert'
  | 'bridge'
  | 'edit'
  | 'trash'
  | 'play'
  | 'pause'
  | 'rewind'
  | 'forward'
  | 'target'
  | 'stop'
  | 'expand'
  | 'download'
  | 'share'
  | 'copy'
  | 'spark'
  | 'conjecture'
  | 'command'
  | 'fit'
  | 'chevron'
  | 'library'
  | 'folder'
  | 'fork'
  | 'pin'
  | 'more'
  | 'cube'
  | 'eye'
  | 'rings'
  | 'dim'
  | 'minimap'

const P: Record<IconName, ReactElement> = {
  pathways: (
    <>
      <circle cx="8" cy="8" r="2.1" />
      <path d="M8 5.9V2.6M8 10.1v3.3M5.9 8H2.6M10.1 8h3.3M6.5 6.5 4.2 4.2M9.5 9.5l2.3 2.3M9.5 6.5l2.3-2.3M6.5 9.5l-2.3 2.3" />
    </>
  ),
  // A clock turned back: the run strip's mark (D352), the passes a run makes
  // one after another without a press each.
  history: (
    <>
      <path d="M2.4 8a5.6 5.6 0 1 0 5.6-5.6 6 6 0 0 0-4.2 1.7L2.4 5.4" />
      <path d="M2.4 2.6v2.8h2.8M8 5v3l2.4 1.3" />
    </>
  ),
  // The composer's send (D352): up, into the thread above it.
  arrowUp: <path d="M8 12.8V3.6M4.2 7.4 8 3.6l3.8 3.8" />,
  // A page with its corner turned: your own sources, the documents a check may
  // read (D353) — the glass is search's, and two of them on one pane read as one
  // door twice.
  file: (
    <>
      <path d="M4.2 2.2h5l2.6 2.6v9H4.2z" />
      <path d="M9.2 2.2v2.6h2.6" />
    </>
  ),
  plus: <path d="M8 3.5v9M3.5 8h9" />,
  minus: <path d="M3.5 8h9" />,
  search: (
    <>
      <circle cx="7.2" cy="7.2" r="4" />
      <path d="m10.2 10.2 3 3" />
    </>
  ),
  sun: (
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.6v1.6M8 12.8v1.6M1.6 8h1.6M12.8 8h1.6M3.6 3.6l1.1 1.1M11.3 11.3l1.1 1.1M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1" />
    </>
  ),
  moon: <path d="M13 9.4A5.6 5.6 0 0 1 6.6 3a5.6 5.6 0 1 0 6.4 6.4Z" />,
  key: (
    <>
      <circle cx="5.4" cy="5.4" r="2.6" />
      <path d="m7.4 7.4 5.2 5.2M10.6 10.6l1.4-1.4M12 12l1.2-1.2" />
    </>
  ),
  // A padlock, shut: a copy being read first, which nothing changes until it is
  // taken over (the thread's head, D362).
  lock: (
    <>
      <rect x="3.6" y="7.2" width="8.8" height="6.4" rx="1.6" />
      <path d="M5.6 7.2V5.2a2.4 2.4 0 0 1 4.8 0v2" />
    </>
  ),
  /** Sliders, not a cog. A cog's teeth are sub-pixel at the 13-14px this is
   *  drawn at in a 1.5-stroke 16-box, and the glyph collapses into a blob. The
   *  wrapper sets fill="none" stroke="currentColor", so the circles render as
   *  outlines the way the paths do. */
  settings: (
    <>
      <path d="M2.6 4.4h10.8M2.6 8h10.8M2.6 11.6h10.8" />
      <circle cx="6" cy="4.4" r="1.5" />
      <circle cx="10.4" cy="8" r="1.5" />
      <circle cx="5.2" cy="11.6" r="1.5" />
    </>
  ),
  panelLeft: (
    <>
      <rect x="2.2" y="2.8" width="11.6" height="10.4" rx="2" />
      <path d="M6.4 2.8v10.4" />
    </>
  ),
  panelRight: (
    <>
      <rect x="2.2" y="2.8" width="11.6" height="10.4" rx="2" />
      <path d="M9.6 2.8v10.4" />
    </>
  ),
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  check: <path d="m3.4 8.4 3 3 6.2-7" />,
  alert: (
    <>
      <path d="M8 3v5.4" />
      <circle cx="8" cy="11.6" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  info: (
    <>
      <circle cx="8" cy="8" r="6.1" />
      <path d="M8 7.5v3.7" />
      <circle cx="8" cy="4.9" r=".75" fill="currentColor" stroke="none" />
    </>
  ),
  deepen: <path d="M8 2.6v9.2M4.6 8.6 8 12l3.4-3.4" />,
  challenge: <path d="M9.4 1.8 3.6 9h4l-1 5.2L12.4 7h-4l1-5.2Z" />,
  diverge: (
    <>
      <path d="M2.6 13.4 13.4 2.6M13.4 2.6H9.2M13.4 2.6v4.2" />
      <path d="M2.6 6.4v-3.8h3.8" />
    </>
  ),
  experiment: (
    <>
      <path d="M6.4 2.2v3.9L3 11.4a1.5 1.5 0 0 0 1.3 2.3h7.4a1.5 1.5 0 0 0 1.3-2.3L9.6 6.1V2.2" />
      <path d="M5.6 2.2h4.8M4.6 9.6h6.8" />
    </>
  ),
  invert: (
    <>
      <path d="M3.2 6.6a5 5 0 0 1 8.6-2.2l1.4 1.4" />
      <path d="M13.2 2.6v3.2H10" />
      <path d="M12.8 9.4a5 5 0 0 1-8.6 2.2L2.8 10.2" />
      <path d="M2.8 13.4v-3.2H6" />
    </>
  ),
  bridge: (
    <>
      <path d="M6.4 9.6 9.6 6.4" />
      <path d="M9 4.6 10.4 3.2a2.7 2.7 0 0 1 3.8 3.8L12.8 8.4" />
      <path d="M7 11.4 5.6 12.8A2.7 2.7 0 0 1 1.8 9L3.2 7.6" />
    </>
  ),
  edit: <path d="M11.2 2.4 13.6 4.8 5.6 12.8 2.4 13.6l.8-3.2 8-8Z" />,
  trash: (
    <>
      <path d="M2.8 4.4h10.4M6.2 4.4V2.8h3.6v1.6M4.4 4.4l.7 9h5.8l.7-9" />
    </>
  ),
  play: <path d="M4.8 3.2 12.4 8l-7.6 4.8V3.2Z" />,
  pause: <path d="M5.6 3.4v9.2M10.4 3.4v9.2" />,
  rewind: <path d="M12.4 3.2 5.6 8l6.8 4.8V3.2ZM3.6 3.2v9.6" />,
  forward: <path d="M3.6 3.2 10.4 8l-6.8 4.8V3.2ZM12.4 3.2v9.6" />,
  target: (
    <>
      <circle cx="8" cy="8" r="5.4" />
      <circle cx="8" cy="8" r="1.9" />
    </>
  ),
  stop: <rect x="4.2" y="4.2" width="7.6" height="7.6" rx="1.6" />,
  /** The graph footer's three switches (D369). Rings: the depth rings behind
   *  the graph, two of them round a centre. Dim: half a disc lit, half put
   *  out — the graph with what is unrelated faded. Minimap: a frame with the
   *  overview in its lower right corner, where the minimap stands. The filled
   *  parts set their own fill; the wrapper strokes everything else. */
  rings: (
    <>
      <circle cx="8" cy="8" r="5.6" />
      <circle cx="8" cy="8" r="2.6" />
    </>
  ),
  dim: (
    <>
      <circle cx="8" cy="8" r="5.6" />
      <path d="M8 2.4a5.6 5.6 0 0 1 0 11.2Z" fill="currentColor" stroke="none" />
    </>
  ),
  minimap: (
    <>
      <rect x="2.2" y="3" width="11.6" height="10" rx="1.8" />
      <rect x="8.2" y="8" width="3.6" height="3" rx="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  expand: (
    <>
      <circle cx="8" cy="8" r="1.6" />
      <path d="M8 6.4V2.4M8 9.6v4M9.6 8h4M6.4 8h-4" />
    </>
  ),
  download: <path d="M8 2.6v7.6M4.8 7.4 8 10.6l3.2-3.2M3 13.2h10" />,
  /**
   * The UNIVERSAL share mark: a tray open at the top with an arrow rising
   * through the gap. It replaced the three-connected-dots form, which is a share
   * glyph on exactly one platform and reads as "graph", "network" or "branch"
   * everywhere else — on a surface whose whole subject is graphs, that is the
   * one wrong association a glyph could carry.
   *
   * It also de-collides the row: `fork` was deliberately drawn on the old
   * share's r=2 nodes, so the two acts sitting side by side on every row were
   * the same three dots joined two ways. They are now different shapes as well
   * as different acts.
   *
   * The tray's 1.8 corner radius is `copy`'s, and its 2.4→13.6 span is
   * `panelLeft`'s box, so the new mark is drawn out of the vocabulary already
   * here rather than imported whole. The gap the arrow rises through is 5.2
   * wide against a 5.0 arrowhead: any tighter and the two collide at 13px.
   */
  share: (
    <>
      <path d="M5.4 6.8H4.2A1.8 1.8 0 0 0 2.4 8.6v3.2a1.8 1.8 0 0 0 1.8 1.8h7.6a1.8 1.8 0 0 0 1.8-1.8V8.6a1.8 1.8 0 0 0-1.8-1.8h-1.2" />
      <path d="M8 10.4V2.4" />
      <path d="m5.5 4.9 2.5-2.5 2.5 2.5" />
    </>
  ),
  copy: (
    <>
      <rect x="5.6" y="5.6" width="8" height="8" rx="1.8" />
      <path d="M10.6 5.6V4.4a1.8 1.8 0 0 0-1.8-1.8H4.4a1.8 1.8 0 0 0-1.8 1.8v4.4a1.8 1.8 0 0 0 1.8 1.8h1.2" />
    </>
  ),
  /**
   * Books on a shelf: the Library, everything saved on this device (D390).
   *
   * It was `layers`, stacked sheets, which said "several of something" and read
   * as a stack of cards; the owner asked for the books. Two upright and one
   * leaning on them, their feet on one line, so the three read as a shelf and
   * not as bars. The uprights are `copy`'s rounded rectangles at a smaller
   * corner, each with a band across its spine, and the leaning one is the same
   * book turned 15° so its top rests on its neighbour's shoulder. Every door to
   * the Library draws it: the sidebar's row and rail, the palette, the sheet.
   */
  library: (
    <>
      <rect x="2.4" y="2.6" width="2.8" height="10.8" rx="0.6" />
      <rect x="6" y="4.2" width="2.8" height="9.2" rx="0.6" />
      <path d="M2.4 5.4h2.8M6 6.8h2.8" />
      <path d="M11.2 13.4l2.51-.67-2.28-8.5-2.51.67z" />
    </>
  ),
  /**
   * A project's mark on its heading in the explorations sidebar.
   *
   * The folder left the set with D56, and the rule it left under still holds: a
   * glyph nothing draws is a glyph the set should not carry. It is back because
   * something draws it — the sidebar's project headings are rows now, each led by
   * the mark the reference the owner pointed at leads with (D89). A HEADING, not
   * an act: D56's objection was to a fourth button on every row, and this sits on
   * no button at all. Tab at the same 1.2 radius as `copy`'s corners, so the two
   * containers in the set are drawn in one hand.
   */
  folder: (
    <path d="M2.5 4.9a1.2 1.2 0 0 1 1.2-1.2h3l1.5 1.6h4.1a1.2 1.2 0 0 1 1.2 1.2v5.3a1.2 1.2 0 0 1-1.2 1.2H3.7a1.2 1.2 0 0 1-1.2-1.2z" />
  ),
  /**
   * One line splitting into two: the copy that keeps its source.
   *
   * The r=2 nodes were `share`'s once, and were chosen BECAUSE the two acts are
   * neighbours on every row — a fork drawn in a different hand would have read
   * as a different kind of act. Share has since moved to the universal tray, so
   * these dots are the fork's own now, and the pair reads as two acts rather
   * than one shape joined two ways. The crossbar's 1.8 corner radius is
   * `copy`'s, which is the other act this one is a neighbour of.
   */
  fork: (
    <>
      <circle cx="4.5" cy="3.8" r="2" />
      <circle cx="11.5" cy="3.8" r="2" />
      <circle cx="8" cy="12.2" r="2" />
      <path d="M4.5 5.8v1.4a1.8 1.8 0 0 0 1.8 1.8h3.4a1.8 1.8 0 0 0 1.8-1.8V5.8" />
      <path d="M8 9v1.2" />
    </>
  ),
  /**
   * A pushpin, upright: the act on a sidebar row that keeps an exploration at
   * the head of the list, and the same button takes it down (D99).
   *
   * Drawn out of the set's own spans rather than imported: the head's bar and
   * the needle's point sit on the 2.6→13.6 line `share`'s tray and `download`'s
   * arrow use, and the body flares to the 4.8→11.2 width `fork`'s crossbar
   * spans, so at 13px it reads beside the fork and the share as one more act
   * in the same hand. Upright rather than tilted because a diagonal pin is a
   * pin someone has knocked, and at this size the tilt costs the needle.
   */
  pin: (
    <>
      <path d="M5.6 2.6h4.8M6.8 2.6v3.4L4.8 8.4v1.4h6.4V8.4L9.2 6V2.6" />
      <path d="M8 9.8v3.8" />
    </>
  ),
  /**
   * Three dots down the middle: the row's one control in the explorations
   * sidebar, which opens the row's menu (D100). Drawn as three rings of the
   * smallest radius the 1.5 stroke closes into a dot at 13px — an outlined
   * circle this small paints solid, so the wrapper's `fill="none"` costs
   * nothing here — spaced to the same 4.4 the sun's rays and the settings
   * sliders sit on.
   */
  more: (
    <>
      <circle cx="8" cy="3.6" r=".8" />
      <circle cx="8" cy="8" r=".8" />
      <circle cx="8" cy="12.4" r=".8" />
    </>
  ),
  spark: (
    <>
      <path d="M8 1.8 9.4 6l4.2 1.4-4.2 1.4L8 13l-1.4-4.2L2.4 7.4 6.6 6 8 1.8Z" />
      <path d="M12.8 11.6 13.4 13l1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4Z" />
    </>
  ),
  command: (
    <path d="M5.4 2.6a1.8 1.8 0 1 0 1.8 1.8v7.2a1.8 1.8 0 1 0 1.8-1.8H4.4a1.8 1.8 0 1 0 1.8 1.8V4.4a1.8 1.8 0 1 0-1.8 1.8h7.2a1.8 1.8 0 1 0-1.8-1.8" />
  ),
  // The canvas glyph at icon size: a hollow centre inside a containing ring —
  // a proposal nothing outside the model has tested yet.
  conjecture: (
    <>
      <circle cx="8" cy="8" r="2.2" />
      <circle cx="8" cy="8" r="5.6" />
    </>
  ),
  fit: (
    <>
      <path d="M2.6 6V3.4a.8.8 0 0 1 .8-.8H6M10 2.6h2.6a.8.8 0 0 1 .8.8V6M13.4 10v2.6a.8.8 0 0 1-.8.8H10M6 13.4H3.4a.8.8 0 0 1-.8-.8V10" />
    </>
  ),
  chevron: <path d="m6 4 4 4-4 4" />,
  // An isometric cube: the outer hexagon plus the three edges meeting at the near
  // corner. The only signifier for "spatial" that reads at 16px without a caption.
  cube: (
    <>
      <path d="M8 1.9 13.7 5.2v6.4L8 14.9 2.3 11.6V5.2Z" />
      <path d="M2.3 5.2 8 8.5l5.7-3.3M8 8.5v6.4" />
    </>
  ),
  // What is drawn and what is hidden — the visibility switches wear it.
  eye: (
    <>
      <path d="M1.9 8S4.2 3.7 8 3.7 14.1 8 14.1 8 11.8 12.3 8 12.3 1.9 8 1.9 8Z" />
      <circle cx="8" cy="8" r="1.9" />
    </>
  ),
}

export function Icon({
  name,
  size = 16,
  strokeWidth = 1.5,
  ...rest
}: {
  name: IconName
  size?: number
  strokeWidth?: number
} & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {P[name]}
    </svg>
  )
}
